import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
  DateRangePicker,
  defaultStaticRanges,
  defaultInputRanges
} from "react-date-range";
import { enUS } from "date-fns/locale";

import BasicModal from "components/modals/base/default/default-modal.component";
import {
  DATE_RANGE_SCOPES,
  DATE_RANGE_STATE_KEYS,
  setDateRange
} from "store/date-range/date-range.action";

import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import "./date-range-filter.styles.scss";

const buildRangeSelection = (startDate, endDate) => [
  {
    startDate: startDate ? new Date(startDate) : new Date(),
    endDate: endDate ? new Date(endDate) : new Date(),
    key: "selection"
  }
];

const DateRangeFilter = ({
  mode = "redux",
  scope = DATE_RANGE_SCOPES.AUDIT_LOGS,
  startDate: startDateProp,
  endDate: endDateProp,
  onApply
}) => {
  const dispatch = useDispatch();
  const pickerLocale = useMemo(() => enUS, []);
  const isReduxMode = mode === "redux";

  const reduxRange = useSelector(state => state[DATE_RANGE_STATE_KEYS[scope]]);
  const startDate = isReduxMode ? reduxRange?.startDate : startDateProp;
  const endDate = isReduxMode ? reduxRange?.endDate : endDateProp;

  const [showDatePicker, setShowDatePicker] = useState(false);
  const [tempDateRange, setTempDateRange] = useState(() =>
    buildRangeSelection(startDate, endDate)
  );

  const openModal = () => {
    setTempDateRange(buildRangeSelection(startDate, endDate));
    setShowDatePicker(true);
  };

  const normalizeSelection = (rangeStart, rangeEnd) => ({
    startDate: moment(rangeStart).startOf("day").toDate(),
    endDate: moment(rangeEnd).endOf("day").toDate()
  });

  const handleDateChange = ({ selection }) => {
    const { startDate: rangeStart, endDate: rangeEnd } = selection;
    const normalized = normalizeSelection(rangeStart, rangeEnd);
    setTempDateRange([
      {
        ...selection,
        ...normalized,
        key: "selection"
      }
    ]);
  };

  const handleApplyFilter = () => {
    const selection = tempDateRange[0];
    if (!selection?.startDate || !selection?.endDate) {
      return;
    }

    const start = moment(selection.startDate).startOf("day").toDate();
    const end = moment(selection.endDate).endOf("day").toDate();

    if (isReduxMode) {
      dispatch(setDateRange(start, end, "custom", scope));
    }

    if (onApply) {
      onApply(start, end);
    }

    setShowDatePicker(false);
  };

  if (!startDate || !endDate) {
    return null;
  }

  return (
    <div className="date-range-filter">
      <div
        role="button"
        tabIndex={0}
        onClick={openModal}
        onKeyDown={event => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openModal();
          }
        }}
        className="date-filter cp row align-center"
        aria-label="Filter by date"
      >
        <i className="fas fa-filter text-15 text-grey-300 mr-10" aria-hidden="true" />
        <span>{moment(startDate).format("LL")}</span>
        <span className="mx-5">-</span>
        <span>{moment(endDate).format("LL")}</span>
      </div>

      {showDatePicker ? (
        <BasicModal
          title="Date Range"
          setShowModal={setShowDatePicker}
          onClose={() => {}}
          width="auto"
          height="auto"
        >
          <div className="p-10">
            <DateRangePicker
              onChange={handleDateChange}
              showSelectionPreview={true}
              moveRangeOnFirstSelection={false}
              months={2}
              ranges={tempDateRange}
              direction="horizontal"
              preventSnapRefocus={true}
              calendarFocus="backwards"
              locale={pickerLocale}
              staticRanges={defaultStaticRanges}
              inputRanges={defaultInputRanges}
            />
            <div className="w-100 p-10 row justify-end">
              <button
                type="button"
                onClick={handleApplyFilter}
                className="btn btn--success btn--lg"
              >
                Apply filter
              </button>
            </div>
          </div>
        </BasicModal>
      ) : null}
    </div>
  );
};

DateRangeFilter.propTypes = {
  mode: PropTypes.oneOf(["redux", "local"]),
  scope: PropTypes.string,
  startDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  onApply: PropTypes.func
};

export default DateRangeFilter;
